import { createTheme } from '@mui/material/styles';

// Color palette from client
const colors = {
  darkBlue: '#333968',      // Darkest blue - for dark backgrounds, text
  mediumBlue: '#537ebc',    // Medium blue - primary main
  deepBlue: '#395093',      // Deep blue - primary dark, secondary
  beige: '#dbcebe',         // Beige - for backgrounds, accents (not secondary)
  brown: '#794f3d',         // Brown - for accents, borders (not secondary)
};

const theme = createTheme({
  palette: {
    primary: {
      main: colors.mediumBlue,      // #537ebc - Main primary color
      light: '#6b96d0',              // Lighter shade for hover states
      dark: colors.deepBlue,         // #395093 - Darker shade
      darker: colors.darkBlue,       // #333968 - Darkest shade
      contrastText: '#ffffff',       // White text on primary
    },
    secondary: {
      main: colors.deepBlue,         // #395093 - Secondary main (blue shade)
      light: '#4d6ba8',              // Lighter blue shade
      dark: colors.darkBlue,         // #333968 - Darker blue shade
      contrastText: '#ffffff',       // White text on secondary
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
    },
    info: {
      main: colors.mediumBlue,       // #537ebc
      light: '#6b96d0',
      dark: colors.deepBlue,         // #395093
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    background: {
      default: '#f5f5f5',            // Light gray background
      paper: '#DCEAF6',              // White paper/card background
      light: colors.beige,           // #dbcebe - Light beige for subtle backgrounds
    },
    text: {
      primary: colors.darkBlue,      // #333968 - Main text color
      secondary: '#666666',          // Gray for secondary text
      disabled: '#999999',           // Gray for disabled text
    },
    divider: '#e0e0e0',
    // Custom colors for specific use cases
    custom: {
      brown: colors.brown,           // #794f3d - For borders, accents
      beige: colors.beige,           // #dbcebe - For backgrounds
      darkBlue: colors.darkBlue,     // #333968 - For dark sections
      mediumBlue: colors.mediumBlue, // #537ebc - Primary blue
      deepBlue: colors.deepBlue,     // #395093 - Deep blue
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      color: colors.darkBlue,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: colors.darkBlue,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: colors.darkBlue,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: colors.darkBlue,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: colors.darkBlue,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      color: colors.darkBlue,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: colors.darkBlue,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#666666',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.darkBlue, // #333968 - Dark blue app bar
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover fieldset': {
              borderColor: colors.mediumBlue,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.mediumBlue,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
  },
});

export default theme;

